const DEFAULT_SUFFIX = " | LifeWestWay";

function truncateWords(value: string, maxLength: number) {
  if (value.length <= maxLength) return value;
  return value.slice(0, maxLength).replace(/\s+\S*$/, "").replace(/[,:;–—-]+$/, "").trim();
}

export function makeSeoTitle(title: string, fallbackYear = "2026", suffix = DEFAULT_SUFFIX) {
  const maxCoreLength = 60 - suffix.length;
  const unbrandedTitle = title.replace(/\s*(?:\||—|–)\s*LifeWestWay\s*$/i, "").trim();
  const year = unbrandedTitle.match(/20\d{2}(?:[–-]\d{2}|\/\d{2})?/)?.[0] ?? fallbackYear;
  let core = unbrandedTitle
    .replace(/\((20\d{2}(?:[–-]\d{2}|\/\d{2})?)(?:\s+(?:Guide|Ranking|Case Study))?\)/gi, "$1")
    .replace(/\((?:Guide|The .*?Guide|Case Study|Ranking)\)/gi, "")
    .replace(/\s*[:—–]\s+.*/, "")
    .replace(/^Step-by-Step Guide to Applying for an?\s+/i, "")
    .replace(/^How to Apply for an?\s+/i, "")
    .replace(/^Complete Guide to\s+/i, "")
    .replace(/^The Ultimate Guide to (?:the )?/i, "")
    .replace(/^Understanding the\s+/i, "")
    .replace(/^Why\s+/i, "")
    .replace(/^Top 10\s+/i, "Top ")
    .replace(/Computer Science and Engineering/i, "CS & Engineering")
    .replace(/International Students/i, "Students")
    .replace(/Permanent Residency/i, "PR")
    .replace(/United States/i, "USA")
    .replace(/Australian Student Visa/i, "Australia Student Visa")
    .replace(/Canadian Student Visa/i, "Canada Student Visa")
    .replace(/Italian Student Visa/i, "Italy Student Visa")
    .replace(/Portuguese Universities/i, "Portugal Universities")
    .replace(/\s+/g, " ")
    .trim();

  if (year && !core.includes(year)) core = `${core} ${year}`;

  if (core.length > maxCoreLength) {
    const compact = core
      .replace(/ for Students/i, "")
      .replace(/ for New Residents/i, "")
      .replace(/ for New Immigrants/i, "")
      .replace(/ for New Arrivals/i, "")
      .replace(/ in the USA/i, " USA")
      .replace(/ in Australia/i, " Australia")
      .replace(/ in Canada/i, " Canada")
      .replace(/ in Italy/i, " Italy")
      .replace(/ in Portugal/i, " Portugal")
      .replace(/ in the UK/i, " UK")
      .replace(/International/i, "Intl")
      .replace(/Universities/i, "Unis")
      .replace(/Government/i, "Govt")
      .replace(/Explained/i, "Guide")
      .replace(/Complete/i, "Full")
      .replace(/Opportunities/i, "Jobs")
      .replace(/Pathways/i, "Routes")
      .replace(/Requirement(s)?/i, "Rules")
      .replace(/\s+/g, " ")
      .trim();
    core = compact;
  }

  if (core.length > maxCoreLength) {
    const reserve = year && core.includes(year) ? year.length + 1 : 0;
    const withoutYear = year
      ? core.replace(year, "").replace(/\s*[:—–]\s*$/, "").replace(/\s+(?:in|for|to)\s*$/i, "").trim()
      : core;
    const base = truncateWords(withoutYear, maxCoreLength - reserve);
    core = year && reserve ? `${base} ${year}` : truncateWords(core, maxCoreLength);
  }

  return `${core}${suffix}`;
}
const STOPWORD_TAIL = /\s+(?:and|or|the|a|an|of|for|in|on|to|by|with|from|how|what|is|are|at|as)$/i;

function trimTail(value: string) {
  let out = value.trim();
  for (let i = 0; i < 4; i++) {
    const next = out.replace(/[\s,;:–—-]+$/, "").replace(STOPWORD_TAIL, "");
    if (next === out) break;
    out = next;
  }
  return out.replace(/[\s,;:–—-]+$/, "").trim();
}

/**
 * Shortens a long editorial headline into a clean <= 60 char <title>.
 * Keeps natural wording (no forced year injection, no mid-word cuts).
 */
// Hand-tuned short titles for headlines that would otherwise truncate awkwardly
// (and for pages that must stay unique against a near-identical sibling).
const SHORT_TITLE_OVERRIDES: Record<string, string> = {
  "how to get home insurance quotes & cheaper car rates in 2026":
    "Home Insurance Quotes & Cheaper Car Rates 2026",
  "home insurance quotes & cheaper car insurance bundles in 2026":
    "Home & Car Insurance Bundle Quotes 2026",
};

export function makeShortTitle(title: string, suffix = DEFAULT_SUFFIX) {
  const max = 60 - suffix.length;
  let core = title
    .replace(/\s*(?:\||—|–)\s*LifeWestWay\s*$/i, "")
    .replace(/\s+/g, " ")
    .trim();

  const override = SHORT_TITLE_OVERRIDES[core.toLowerCase()];
  if (override) return `${override}${suffix}`;


  if (core.length > max) {
    // Prefer the leading clause before ":" / "?" / "–" when it is substantial.
    const lead = core.split(/\s*(?::|\?|—|–|\|)\s+/)[0]?.trim() ?? core;
    if (lead.length >= 20 && lead.length <= max) return `${trimTail(lead)}${suffix}`;
    if (lead.length < core.length && lead.length > max) core = lead;
  }

  if (core.length <= max) return `${trimTail(core)}${suffix}`;
  let cut = core.slice(0, max + 1).replace(/\s+\S*$/, "");
  // drop an unclosed parenthetical fragment and dangling modifiers
  if ((cut.match(/\(/g)?.length ?? 0) > (cut.match(/\)/g)?.length ?? 0)) {
    cut = cut.slice(0, cut.lastIndexOf("("));
  }
  cut = cut.replace(/\s+(?:artificial|personal|top|best|new|other|more|complete|essential|regional)$/i, "");
  return `${trimTail(cut)}${suffix}`;
}
